import { motion } from "framer-motion";
import { ArrowRight, Baby, BookOpen, Sprout, Megaphone, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/hooks/usePageMeta";

const FOCUS = [
  { icon: Baby, title: "Children's Ministry", text: "Nurturing the faith of the next generation.", tone: { ring: "border-t-emerald", chip: "bg-emerald/15 text-emerald" } },
  { icon: BookOpen, title: "Adult Bible Discipleship", text: "Helping adults grow deep in God's Word.", tone: { ring: "border-t-gold", chip: "bg-gold/20 text-gold-dark" } },
  { icon: Sprout, title: "Spiritual Growth", text: "Encouraging maturity and life in Christ.", tone: { ring: "border-t-navy-700", chip: "bg-navy/10 text-navy-700" } },
  { icon: Megaphone, title: "Evangelism", text: "Sharing the love of Jesus with others.", tone: { ring: "border-t-ruby", chip: "bg-ruby/10 text-ruby" } },
];

const CHARACTERS = [
  { img: "/ju/jesus.png", name: "Jesus", tag: "The heart of the story" },
  { img: "/ju/peter.png", name: "Peter", tag: "Faith through the storm" },
  { img: "/ju/mary-magdalene.png", name: "Mary Magdalene", tag: "A life made new" },
];
const MORE = [
  { img: "/ju/john.png", name: "John" },
  { img: "/ju/mary.png", name: "Mary" },
  { img: "/ju/moses.png", name: "Moses" },
  { img: "/ju/david.png", name: "David" },
  { img: "/ju/noah.png", name: "Noah" },
];

export default function JesusUnited() {
  usePageMeta("Jesus United",
    "Jesus United, founded by the founders of Lord Almighty — children's ministry, adult Bible discipleship, spiritual growth, and evangelism.");
  return (
    <div className="overflow-x-clip">
      <PageHero
        eyebrow="A ministry we founded"
        title="Jesus United"
        subtitle="Founded by the founders of Lord Almighty, Jesus United is a ministry focused on children's ministry, adult Bible discipleship, spiritual growth, and evangelism — home to the JUniverse, where every life has a story."
        image="/ju-jerusalem.webp"
      />

      {/* INTRO with mascot */}
      <section className="relative overflow-hidden bg-ivory">
        <div className="pointer-events-none absolute -top-16 right-[10%] h-64 w-64 rounded-full bg-gold/20 blur-3xl" />
        <div className="relative mx-auto max-w-3xl px-5 lg:px-8 py-16 lg:py-20">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-gold-dark">
              <Sparkles className="h-4 w-4" /> One Name. One Body. One King.
            </span>
            <h2 className="mt-3 font-display text-4xl lg:text-5xl font-semibold text-navy tracking-tight">
              Know Jesus. Follow Jesus. <span className="text-emerald">Share Jesus.</span>
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Jesus United is a place to grow in faith and walk together — for kids and adults alike.
              Meet the friendly faces of the JUniverse and journey through the stories of Scripture in
              a way the whole family can enjoy.
            </p>
            <Button asChild className="mt-7 rounded-full bg-emerald hover:bg-emerald-dark text-white px-8 py-6 text-base shadow-xl transition-transform hover:scale-105">
              <a href="https://www.jesusunited.org" target="_blank" rel="noopener noreferrer" data-testid="ju-intro-visit">
                Explore JesusUnited.org <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </Reveal>
        </div>
      </section>

      {/* FOCUS cards */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-8 lg:py-12">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {FOCUS.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className={`group h-full rounded-2xl bg-white border border-border border-t-4 ${f.tone.ring} p-7 shadow-[0_8px_32px_rgba(27,42,71,0.06)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(27,42,71,0.14)]`}>
                <span className={`inline-flex h-14 w-14 items-center justify-center rounded-2xl ${f.tone.chip} transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6`}>
                  <f.icon className="h-7 w-7" />
                </span>
                <h3 className="mt-4 font-display text-xl font-semibold text-navy">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* JUNIVERSE characters */}
      <section className="relative overflow-hidden bg-ivory-alt">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 py-20 lg:py-24">
          <Reveal className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.22em] text-gold-dark">Explore the JUniverse</span>
              <h2 className="mt-2 font-display text-4xl lg:text-5xl font-semibold text-navy tracking-tight">Every life has a story.</h2>
            </div>
            <a href="https://www.jesusunited.org/ju-universe" target="_blank" rel="noopener noreferrer" data-testid="ju-meet-characters"
              className="inline-flex items-center gap-1.5 text-emerald font-semibold text-sm border-b-2 border-gold pb-0.5 hover:gap-3 transition-all">
              Meet the characters <ArrowRight className="h-4 w-4" />
            </a>
          </Reveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {CHARACTERS.map((c, i) => (
              <Reveal key={c.name} delay={i * 0.08}>
                <a href="https://www.jesusunited.org/ju-universe" target="_blank" rel="noopener noreferrer"
                  className="group block rounded-3xl overflow-hidden bg-white border border-border shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all">
                  <div className="relative aspect-[4/4.2] flex items-end justify-center overflow-hidden bg-gradient-to-b from-[#FDF7E7] via-[#F4EDDD] to-[#E9DFC8]">
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_28%,rgba(197,160,89,0.3),transparent_62%)]" />
                    <img src={c.img} alt={c.name} className="relative w-[86%] h-[92%] object-contain drop-shadow-[0_18px_28px_rgba(10,20,40,0.22)] transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="text-center px-4 py-5">
                    <p className="font-display font-semibold text-navy text-xl">{c.name}</p>
                    <p className="text-muted-foreground text-sm mt-0.5">{c.tag}</p>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-wrap justify-center gap-5">
              {MORE.map((m) => (
                <a key={m.name} href="https://www.jesusunited.org/ju-universe" target="_blank" rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1.5 hover:-translate-y-1 transition-transform">
                  <div className="h-20 w-20 md:h-24 md:w-24 rounded-full bg-gradient-to-b from-[#FDF7E7] to-[#E9DFC8] border-2 border-gold/40 flex items-end justify-center overflow-hidden shadow-sm">
                    <img src={m.img} alt={m.name} className="w-[88%] h-[90%] object-contain object-bottom" />
                  </div>
                  <span className="text-[10px] tracking-[0.12em] font-bold text-navy/70">{m.name.toUpperCase()}</span>
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA band on the feeding scene */}
      <section className="relative overflow-hidden text-ivory">
        <div className="absolute inset-0">
          <img src="/feed5000.webp" alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900/95 via-navy/80 to-navy/45" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-32">
          <Reveal className="max-w-xl">
            <h2 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight drop-shadow-[0_8px_24px_rgba(10,20,40,0.55)]">
              Learn more at JesusUnited.org
            </h2>
            <p className="mt-4 text-ivory/90 leading-relaxed drop-shadow-[0_4px_14px_rgba(10,20,40,0.5)]">
              For details about programs and how to get involved, please visit the Jesus United
              website. Inquiries are directed to adults; we do not collect children's personal
              information.
            </p>
            <Button asChild className="mt-8 rounded-full bg-gold hover:bg-gold-dark text-navy px-9 py-6 text-base shadow-2xl transition-transform hover:scale-105">
              <a href="https://www.jesusunited.org" target="_blank" rel="noopener noreferrer" data-testid="jesusunited-visit">
                Visit JesusUnited.org <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </Reveal>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-transparent via-gold to-transparent" />
      </section>
    </div>
  );
}
